import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';
import { useRetrieveAllWorkspaceBookConnections } from '@/generated/hooks/bdk/v2/services/library';
import {
  filterConnectionsByStage,
  useReactivateConnection,
  useWaitForConnectionToBeReady,
} from '@/modules/integrations';
import { captureException } from '@/modules/sentry';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import {
  getSelectedConnectionDescriptors,
  isSelectedConnectionInactive,
} from '../utils/connection-state-utils';

/**
 * Params for {@link useInactiveConnectionGate}.
 */
export interface IUseInactiveConnectionGateParams {
  /** Integration (book) name the connections belong to */
  selectedIntegration?: string;
  /** Currently selected connection id */
  selectedConnection?: string;
  /**
   * Whether the gate is active. Pass `false` while the dialog is closed and in
   * read-only dialogs; turning it off cancels any reactivation in progress.
   */
  enabled?: boolean;
}

/**
 * Options for a single {@link IInactiveConnectionGate.runWithActiveConnection} call.
 */
export interface IRunWithActiveConnectionOptions {
  /**
   * Whether the action writes the trigger. Saving needs a connection that is
   * fully back up; moving to another step does not.
   */
  waitForReady?: boolean;
}

type TConnectionAction = 'idle' | 'reactivating' | 'waiting';

// The poll treats INACTIVE as terminal, so a state the backend has not flipped
// yet would otherwise read as a failure.
const INACTIVE_RETRY_LIMIT = 3;
const INACTIVE_RETRY_DELAY_MS = 2000;

const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

interface IReadinessAttempt {
  connectionId: string;
  promise: Promise<boolean>;
  failed: boolean;
}

/**
 * Primary-action labels a dialog can show while its connection is prepared.
 */
export interface IConnectionActionLabels {
  /** Shown while waiting for an already-requested reactivation to come up */
  waiting: string;
  /** Shown while the action will reactivate the connection first */
  reactivate: string;
  /** The dialog's own label, used when no reactivation is involved */
  default: string;
}

/**
 * How a dialog's primary action should be presented.
 */
export interface IConnectionAction {
  /** Label for the primary button */
  label: string;
  /** Whether to prefix the label with the reactivate icon */
  showPowerIcon: boolean;
}

/**
 * Return value of {@link useInactiveConnectionGate}.
 */
export interface IInactiveConnectionGate {
  /** True when the selected connection needs a reactivation before saving */
  isSelectedConnectionInactive: boolean;
  /** True while reactivating or waiting for the connection to come back up */
  isPreparingConnection: boolean;
  /** True while waiting for an already-requested reactivation to come up */
  isWaitingForConnection: boolean;
  /** Runs `continueAction`, reactivating the selected connection first if needed */
  runWithActiveConnection: (
    continueAction: () => void,
    options?: IRunWithActiveConnectionOptions,
  ) => Promise<void>;
}

/**
 * Resolves the primary button's label and icon from the gate's state, so every
 * trigger dialog presents a reactivation the same way.
 *
 * @param gate - Gate returned by {@link useInactiveConnectionGate}
 * @param labels - The dialog's translated labels for each state
 * @returns The label to render and whether to show the reactivate icon
 */
export function getConnectionAction(
  {
    isSelectedConnectionInactive,
    isPreparingConnection,
    isWaitingForConnection,
  }: IInactiveConnectionGate,
  labels: IConnectionActionLabels,
): IConnectionAction {
  if (isWaitingForConnection) {
    return { label: labels.waiting, showPowerIcon: false };
  }

  // The connection leaves INACTIVE mid-click, so hold the wording until it ends.
  const isReactivating = isSelectedConnectionInactive || isPreparingConnection;

  return {
    label: isReactivating ? labels.reactivate : labels.default,
    showPowerIcon: isReactivating && !isPreparingConnection,
  };
}

/**
 * Gate for trigger dialogs whose selected connection may be scaled to zero.
 *
 * BDK scales idle connections down to `BOOK_CONNECTION_STATE_INACTIVE`. A
 * trigger saved against one is rejected, and one saved while the connection is
 * still coming back up is accepted but then fails, so the save has to wait for
 * `READY`. Steps before the save don't: reactivation starts on the first
 * gated click and keeps rolling out while the user fills in the remaining
 * steps, so by the time they save the wait is usually over.
 *
 * It reads the same connection query the configure step uses, so it shares the
 * React Query cache instead of issuing a second request.
 *
 * @param params - Selected integration/connection and whether the gate applies
 * @returns Inactive flag, reactivation progress, and the wrapped action runner
 */
export function useInactiveConnectionGate({
  selectedIntegration,
  selectedConnection,
  enabled = true,
}: IUseInactiveConnectionGateParams): IInactiveConnectionGate {
  const { t } = useTranslation('automation-inputs');

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: connectionsData } = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    {
      page_size: 100,
      filter: selectedIntegration
        ? FilterBuilder.create()
            .equals('book_name', selectedIntegration)
            .build()
        : undefined,
    },
    {
      query: {
        enabled: Boolean(
          enabled && organizationId && workspaceId && selectedIntegration,
        ),
      },
    },
  );

  const draftConnections = useMemo(
    () =>
      filterConnectionsByStage(
        connectionsData?.data?.book_connections,
        'draft',
      ),
    [connectionsData?.data?.book_connections],
  );

  const { reactivateConnection } = useReactivateConnection(
    organizationId,
    workspaceId,
  );
  const waitForConnectionToBeReady = useWaitForConnectionToBeReady();

  // Local, so one dialog's action doesn't spin on another row's reactivation.
  const [connectionAction, setConnectionAction] =
    useState<TConnectionAction>('idle');

  // The list query lags behind the reactivation we just requested, so remember
  // what we acted on rather than waiting for it to stop reporting INACTIVE.
  const [reactivatedConnection, setReactivatedConnection] = useState<string>();

  const isInactive =
    enabled &&
    selectedConnection !== reactivatedConnection &&
    isSelectedConnectionInactive(draftConnections, selectedConnection);

  const readinessRef = useRef<IReadinessAttempt | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);

  const abortReadiness = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = undefined;
    readinessRef.current = undefined;
  }, []);

  // Dialogs stay mounted while closed, so unmount is not the signal to stop.
  // eslint-disable-next-line no-restricted-syntax -- reason: stop polling a connection the user walked away from
  useEffect(() => {
    if (!enabled) {
      abortReadiness();
      setConnectionAction('idle');
      setReactivatedConnection(undefined);
    }
    return abortReadiness;
  }, [enabled, abortReadiness]);

  const waitUntilReady = useCallback(
    async (connectionId: string): Promise<boolean> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        for (let attempt = 0; attempt <= INACTIVE_RETRY_LIMIT; attempt++) {
          const result = await waitForConnectionToBeReady(
            connectionId,
            controller.signal,
          );
          if (result.isReady) return true;
          // Cancelled means the dialog is gone, so there is nobody to tell.
          if (result.isCancelled) return false;
          if (!result.isInactive || attempt === INACTIVE_RETRY_LIMIT) break;

          await sleep(INACTIVE_RETRY_DELAY_MS);
          if (controller.signal.aborted) return false;
        }

        toast.error(t('trigger.connection.notReadyError'));
        return false;
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              module: 'triggers',
              action: 'waitForConnectionAfterReactivate',
            },
          },
        );
        toast.error(t('trigger.connection.notReadyError'));
        return false;
      }
    },
    [waitForConnectionToBeReady, t],
  );

  const startReadiness = useCallback(
    (connectionId: string): Promise<boolean> => {
      const attempt: IReadinessAttempt = {
        connectionId,
        failed: false,
        promise: Promise.resolve(false),
      };
      attempt.promise = waitUntilReady(connectionId).then(ready => {
        attempt.failed = !ready;
        // Clearing lets the next click reactivate instead of only re-polling;
        // the identity check keeps a superseded attempt from doing it.
        if (!ready && readinessRef.current === attempt) {
          setReactivatedConnection(undefined);
        }
        return ready;
      });
      readinessRef.current = attempt;
      return attempt.promise;
    },
    [waitUntilReady],
  );

  /**
   * The readiness of a reactivation started earlier in this dialog, or
   * `undefined` when the connection was never reactivated here. A failed
   * attempt starts a fresh one so the user can retry by clicking again.
   */
  const pendingReadiness = useCallback(
    (connectionId: string): Promise<boolean> | undefined => {
      const attempt = readinessRef.current;
      if (!attempt || attempt.connectionId !== connectionId) return undefined;
      return attempt.failed ? startReadiness(connectionId) : attempt.promise;
    },
    [startReadiness],
  );

  const runWithActiveConnection = useCallback(
    async (
      continueAction: () => void,
      { waitForReady = false }: IRunWithActiveConnectionOptions = {},
    ) => {
      if (!isInactive || !selectedConnection) {
        // A reactivation kicked off on an earlier step may still be rolling out.
        const readiness =
          waitForReady && selectedConnection
            ? pendingReadiness(selectedConnection)
            : undefined;
        if (readiness) {
          setConnectionAction('waiting');
          try {
            if (!(await readiness)) return;
          } finally {
            setConnectionAction('idle');
          }
        }
        continueAction();
        return;
      }

      setConnectionAction('reactivating');
      try {
        const reactivated = await reactivateConnection(
          getSelectedConnectionDescriptors(
            draftConnections,
            selectedConnection,
          ),
        );
        if (!reactivated) return;
        setReactivatedConnection(selectedConnection);

        const readiness = startReadiness(selectedConnection);
        if (waitForReady) {
          setConnectionAction('waiting');
          if (!(await readiness)) return;
        }

        continueAction();
      } finally {
        setConnectionAction('idle');
      }
    },
    [
      isInactive,
      selectedConnection,
      draftConnections,
      reactivateConnection,
      startReadiness,
      pendingReadiness,
    ],
  );

  return {
    isSelectedConnectionInactive: isInactive,
    isPreparingConnection: connectionAction !== 'idle',
    isWaitingForConnection: connectionAction === 'waiting',
    runWithActiveConnection,
  };
}
